import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@env";
import { Observable } from "rxjs";

import {
  LaborPriceIdUrlDTO,
  PartsOfServiceMaintenance,
  ServiceDTO,
  ServicePartsDTO,
  ServicePriceDefaultDTO,
  ServicePriceReferenceDTO,
  ServiceUpdateDTO
} from "@shared/models/services-dto";
import { PageManager } from "@shared/models/page-manager";
import { LanguageService } from "./language.service";
import { map } from "rxjs/operators";
import { Pagination } from "@shared/models/pagination";

@Injectable({
  providedIn: "root"
})
export class ServicesService {
  private serviceUrl = "service";
  private language: string;

  constructor(
    private http: HttpClient,
    private languageService: LanguageService
  ) {
    this.languageService.getLanguage$().subscribe((res) => {
      this.language = res;
    });
  }

  addNewService(ServiceDTO: ServiceDTO): Observable<any> {
    return this.http.post<any>(
      environment.microserviceParts + this.serviceUrl,
      ServiceDTO
    );
  }

  savePartInService(partInServiceForm): Observable<any> {
    return this.http.post<any>(
      environment.microserviceParts + this.serviceUrl + `/part`,
      partInServiceForm
    );
  }

  getAllServicesByFilters(
    searchFormDTO: any,
    pageManager: PageManager,
    sort = ""
  ) {
    let query = "";

    if (searchFormDTO.name) {
      query += `&name=${searchFormDTO.name}`;
    }
    if (searchFormDTO.serviceCode) {
      query += `&serviceCode=${searchFormDTO.serviceCode}`;
    }
    if (
      typeof searchFormDTO.initialHour === "number" ||
      typeof searchFormDTO.initialMinute === "number"
    ) {
      query += `&initialHour=${searchFormDTO.initialHour}`;
      query += `&initialMinute=${searchFormDTO.initialMinute}`;
    }
    if (
      typeof searchFormDTO.endHour === "number" ||
      typeof searchFormDTO.endMinute === "number"
    ) {
      query += `&endHour=${searchFormDTO.endHour}`;
      query += `&endMinute=${searchFormDTO.endMinute}`;
    }

    return this.languageService.getLanguage$().pipe(
      map((currentLanguage) => {
        const headers = new HttpHeaders({
          "Content-Type": "application/json",
          "Accept-Language": currentLanguage
        });
        return this.http.get<Pagination<ServiceDTO[]>>(
          environment.microserviceParts +
            this.serviceUrl +
            `?size=${pageManager.pageSize}&page=${pageManager.pageIndex}${query}${sort}`,
          { headers }
        );
      })
    );
  }

  getPartsByService(serviceId, pageManager: PageManager, sort = "") {
    return this.languageService.getLanguage$().pipe(
      map((currentLanguage) => {
        const headers = new HttpHeaders({
          "Content-Type": "application/json",
          "Accept-Language": currentLanguage
        });
        return this.http.get<Pagination<ServicePartsDTO[]>>(
          environment.microserviceParts +
            this.serviceUrl +
            `/part?serviceId=${serviceId}&size=${pageManager.pageSize}&page=${pageManager.pageIndex}${sort}`,
          { headers }
        );
      })
    );
  }

  getPartsByServiceMaintenancePlan(
    serviceId,
    pageManager: PageManager,
    sort = "",
    modelId = null
  ) {
    return this.languageService.getLanguage$().pipe(
      map((currentLanguage) => {
        const headers = new HttpHeaders({
          "Content-Type": "application/json",
          "Accept-Language": currentLanguage
        });
        return this.http.get<Pagination<PartsOfServiceMaintenance[]>>(
          environment.microserviceParts +
            this.serviceUrl +
            `/part-derivation?serviceId=${serviceId}&modelId=${modelId}&size=${pageManager.pageSize}&page=${pageManager.pageIndex}${sort}`,
          { headers }
        );
      })
    );
  }

  getServicePriceReferencesByFilters(
    serviceId,
    searchFormDTO,
    pageManager: PageManager,
    sort = ""
  ) {
    let query = "&serviceId=" + serviceId;
    if (searchFormDTO.referenceName) {
      query += `&referenceName=${searchFormDTO.referenceName}`;
    }

    return this.http.get<Pagination<ServicePriceReferenceDTO[]>>(
      `${environment.microserviceParts}labor/price?page=${pageManager.pageIndex}&size=${pageManager.pageSize}${query}${sort}`
    );
  }

  getServicePriceReferenceById(id: number) {
    return this.http.get<ServicePriceReferenceDTO>(
      `${environment.microserviceParts}labor/price/${id}`
    );
  }

  getDefaultPrice(serviceId) {
    return this.http.get<ServicePriceDefaultDTO>(
      `${environment.microserviceParts}service/default-price/${serviceId}`
    );
  }

  getLaborPriceById(entityId) {
    return this.http.get<Observable<LaborPriceIdUrlDTO>>(
      `${environment.microserviceParts}labor/price/${entityId}`
    );
  }

  getPriceServiceApprovalHistory(laborPriceId: number) {
    return this.http.get<any[]>(
      `${environment.microserviceParts}labor/price/request-approval/history/${laborPriceId}`
    );
  }

  updatePriceRef(
    id: number,
    PartPriceReferenceDTO: ServicePriceReferenceDTO
  ): Observable<any> {
    return this.http.put<any>(
      environment.microserviceParts + "labor/price/request-approval/" + id,
      PartPriceReferenceDTO
    );
  }

  findById(id: number): Observable<ServiceDTO> {
    return this.http.get<ServiceDTO>(
      environment.microserviceParts + `${this.serviceUrl}/${id}`
    );
  }

  edit(id: number, ServiceUpdateDTO: ServiceUpdateDTO): Observable<any> {
    return this.http.put<any>(
      environment.microserviceParts + `${this.serviceUrl}/${id}`,
      ServiceUpdateDTO
    );
  }

  deleteService(id: number): Observable<any> {
    return this.http.delete<any>(
      environment.microserviceParts + `${this.serviceUrl}/${id}`
    );
  }

  deletePartOfService(serviceId, partId): Observable<any> {
    return this.http.delete<any>(
      environment.microserviceParts +
        `${this.serviceUrl}/part?serviceId=${serviceId}&partId=${partId}`
    );
  }
}
